import { ProcessGeneral } from 'api/endpoints/processes';
import BaseRepo from 'container/BaseRepo';
import {
  buildBadRequestApiError,
  buildNotFoundApiError,
} from 'container/helpers/errors';
import { ApiResponseAsync, isApiError } from 'container/models';
import { SearchRequestTyped } from 'container/search';
import {
  IGeneralPayablesTaskRepo,
  SearchableGeneralPayablesTask,
} from 'features/generalPayables/services/tasks/IGeneralPayablesTaskRepo';
import {
  FinishTaskValues,
  GeneralPayablesTask,
  GeneralPayablesTaskBase,
  GeneralPayablesTaskToRetry,
} from 'features/generalPayables/types';

import { GeneralPayablesTasksToRetry } from '../typing';

export class GeneralPayablesTaskRepo
  extends BaseRepo
  implements IGeneralPayablesTaskRepo
{
  search = async (
    draft: ProcessGeneral,
    request: Omit<SearchRequestTyped<GeneralPayablesTaskBase>, 'count'>
  ): ApiResponseAsync<GeneralPayablesTaskBase[]> => {
    const link = draft._links?.['searchGeneralPayablesTasks'];

    if (!link) {
      return buildBadRequestApiError(
        new Error('link "search General Payables tasks" is not available')
      );
    }

    const searchResult = await this.helpers.executeSearch<
      GeneralPayablesTaskBase[]
    >(link, request, { convertRequestToSnakeCase: true });
    if (isApiError(searchResult)) {
      return searchResult;
    }
    return searchResult ?? [];
  };

  getById = async (
    draft: ProcessGeneral,
    taskId: string
  ): ApiResponseAsync<GeneralPayablesTaskBase> => {
    const request: SearchRequestTyped<SearchableGeneralPayablesTask> = {
      top: 1,
      filter: {
        operator: 'AND',
        items: [
          {
            field: 'id',
            operation: 'equal',
            value: taskId,
            not: false,
          },
        ],
      },
    };

    const result = await this.search(draft, request);

    if (isApiError(result)) return result;

    if (!result || result.length === 0)
      return buildNotFoundApiError(
        new Error('no General Payables task was found')
      );

    return result[0];
  };

  getByIdExtended = async (
    task: GeneralPayablesTaskBase
  ): ApiResponseAsync<GeneralPayablesTask> => {
    const link = task._links?.['self'];
    if (!link)
      return buildBadRequestApiError(new Error('link "self" is not available'));

    return this.helpers.handleGet(link.href, {
      responseStopTransformPaths: ['data.field_values', 'data.classify_result'],
    });
  };

  delete = (
    task: GeneralPayablesTaskBase | GeneralPayablesTaskToRetry
  ): ApiResponseAsync<void> => {
    const link = task._links?.['delete'];

    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "delete" is not available'))
      );

    return this.helpers.handleDelete(link.href);
  };

  lock = async (
    task: GeneralPayablesTaskBase
  ): ApiResponseAsync<GeneralPayablesTaskBase | undefined> => {
    const link = task._links?.['lock'];

    if (!link)
      return buildBadRequestApiError(new Error(`link "lock" is not available`));

    return this.helpers.handlePost(link.href, undefined);
  };

  lockNext = async (
    draft: ProcessGeneral
  ): ApiResponseAsync<GeneralPayablesTaskBase | undefined> => {
    const link = draft._links?.['lockNextGeneralPayablesTask'];

    if (!link)
      return buildBadRequestApiError(
        new Error(`link "lockNextGeneralPayablesTask" is not available`)
      );

    return this.helpers.handlePost(link.href, undefined);
  };

  skip = async (
    task: GeneralPayablesTaskBase
  ): ApiResponseAsync<GeneralPayablesTaskBase | undefined> => {
    const link = task._links?.['skip'];

    if (!link) {
      return buildBadRequestApiError(new Error('link "skip" is not available'));
    }

    return this.helpers.handlePost(link.href, undefined);
  };

  unlock = async (task: GeneralPayablesTaskBase): ApiResponseAsync<void> => {
    const link = task._links?.['cancel'];

    if (!link)
      return buildBadRequestApiError(
        new Error('link "cancel" is not available')
      );

    return this.helpers.handlePost(link.href, undefined);
  };

  finish = async (
    task: GeneralPayablesTaskBase,
    payload: FinishTaskValues
  ): ApiResponseAsync<void> => {
    const link = task._links?.['finish'];

    if (!link)
      return buildBadRequestApiError(
        new Error('link "finish" is not available')
      );
    return this.helpers.handlePostAction(link.href, payload, {
      requestStopTransformPaths: ['data'],
    });
  };

  getRetryTmsCreateInvoiceTasks = async (
    draft: ProcessGeneral
  ): ApiResponseAsync<GeneralPayablesTasksToRetry[]> => {
    const link = draft._links?.['getGeneralPayablesTasksToRetry'];

    if (!link)
      return buildBadRequestApiError(
        new Error('link "get General Payables tasks to retry" is not available')
      );

    return this.helpers.handleGet(link.href);
  };

  moveTaskToReview = async (
    task: GeneralPayablesTaskToRetry
  ): ApiResponseAsync<void> => {
    const link = task._links?.['sendTaskForReview'];

    if (!link) {
      return buildBadRequestApiError(
        new Error('link "move task for reviewing" is not available')
      );
    }

    return this.helpers.handlePost(link.href, undefined);
  };

  retryTmsCreateInvoice = async (
    task: GeneralPayablesTaskToRetry
  ): ApiResponseAsync<void> => {
    const link = task._links?.['executeTmsCreateInvoice'];
    if (!link)
      return buildBadRequestApiError(
        new Error('link "executeTmsCreateInvoice" is not available')
      );

    return this.helpers.handlePostAction(link.href, undefined);
  };
}
